import React, {Component} from 'react';
import Select from 'react-select';

import Api from '../../Api';
import Button from '../Button';
import {InputField, EmailField, ImageField, MultipleField} from '../FieldComponents';
import {getErrorKey, createAlertMessage} from '../../lib/AdminFunctions';

import {
  USA_STATES,
  CAN_PROVINCES,
  COUNTRIES,
  ISO_TO_COUNTRY_NAME,
  STATE_PROVINCE_SHORTHAND_TO_NAME,
  US_ISO,
  CANADA_ISO,
} from '../../consts';

const ErrorClass = 'error';

export default class BrandLogisticsEditor extends Component {
  constructor(props) {
    super(props);

    this.handleSubmit = this.handleSubmit.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onAddressStateChange = this.onAddressStateChange.bind(this);
    this.onCountryOriginChange = this.onCountryOriginChange.bind(this);
    this.onSecondaryOriginChange = this.onSecondaryOriginChange.bind(this);
    this.onImagesChange = this.onImagesChange.bind(this);
    this.onImagesClear = this.onImagesClear.bind(this);
    this.onImagesNew = this.onImagesNew.bind(this);
    this.onBrandChanged = this.onBrandChanged.bind(this);

    this.state = this.createState(props.logistics);
  }

  componentWillMount() {
    Api.cache.addChangeListener(this.onBrandChanged, Api.cache.eventTypes.BRANDS_CHANGED);
  }

  componentWillUnmount() {
    Api.cache.removeChangeListener(this.onBrandChanged, Api.cache.eventTypes.BRANDS_CHANGED);
  }

  createState(logistics) {
    logistics = logistics || {};

    let {
      token,
      email_address,
      phone_number,
      active,
      address,
      commission_bps,
      minimum_order_amount_cents,
      images,
      email_hero_image,
      email_description,
      made_in,
      made_in_state,
      made_in_city,
      hand_made,
      charitable,
      eco_friendly
    } = logistics;

    images = (images || [])
      .map((image) => {
        return {key: image.token, value: image}
      });

    email_hero_image = email_hero_image || {url: ''};

    let minimum_order_amount_dollars = Api.Utils.formatCents(minimum_order_amount_cents, false, true) || '';

    let secondaryOriginEnabled = (logistics.made_in === CANADA_ISO || logistics.made_in === US_ISO);
    let secondaryOptions = [];
    if (secondaryOriginEnabled) {
      secondaryOptions = (logistics.made_in === US_ISO) ? USA_STATES : CAN_PROVINCES;
    }

    if (made_in) {
      let label = ISO_TO_COUNTRY_NAME[made_in] || made_in;
      made_in = {label: label, value: made_in};
    }

    if (made_in_state) {
      let label = STATE_PROVINCE_SHORTHAND_TO_NAME[made_in_state] || made_in_state;
      made_in_state = {label: label, value: made_in_state};
    }


    address = address || {};
    commission_bps = Api.Utils.formatCents(commission_bps) || '';

    let address_state = null;
    if (address.state) {
      let label = STATE_PROVINCE_SHORTHAND_TO_NAME[address.state];
      if (label) {
        address_state = {label: label, value: address.state};
      } else {
        address_state = {label: address_state, value: address_state};
      }
    }

    let error = (this.state && this.state.error) || false;

    return {
      token,
      error,
      active,
      email_address: email_address || '',
      phone_number: phone_number || '',
      commission_bps: commission_bps,
      images,
      made_in,
      made_in_state,
      made_in_city,
      email_hero_image,
      email_description: email_description || '',
      minimum_order_amount_dollars,
      secondaryOriginEnabled,
      secondaryOptions,
      hand_made,
      charitable,
      eco_friendly,

      // Flatten address properties
      address_name: address.name || '',
      address_address1: address.address1 || '',
      address_address2: address.address2 || '',
      address_postal_code: address.postal_code || '',
      address_city: address.city || '',
      address_state: address_state,
      address_instructions: address.instructions || ''
    };
  }

  onBrandChanged() {
    if (!this.state.token) return;
    Api.getBrand(this.state.token, true)
      .then(brand => {
        this.setState(this.createState(brand));
      });
  }

  onInput(name, input, error) {
    let errorKey = getErrorKey(name);
    this.setState({
      [name]: input,
      [errorKey]: error
    });
  }

  onCountryOriginChange(selectedCountry) {
    let original = (this.state.made_in && this.state.made_in.value) || null;
    let updated = (selectedCountry && selectedCountry.value) || null;

    if (original === updated) return;

    let secondaryOriginEnabled = (updated === US_ISO || updated === CANADA_ISO);
    let secondaryOptions = [];
    if (secondaryOriginEnabled) {
      secondaryOptions = (updated === US_ISO) ? USA_STATES : CAN_PROVINCES;
    }

    this.setState({
      made_in: selectedCountry,
      from: null,
      secondaryOriginEnabled,
      secondaryOptions
    });
  }

  onSecondaryOriginChange(selectedStateProv) {
    if (!this.state.made_in ||
      (this.state.made_in.value !== CANADA_ISO && this.state.made_in.value !== US_ISO)) return;

    this.setState({
      made_in_state: selectedStateProv
    });
  }

  onImagesChange(index, key, value) {
    let images = this.state.images;
    if (index > images.length) {
      images.push({key, value});
    } else {
      images[index] = {key, value};
    }
    this.setState({
      images
    });
  }

  onImagesNew() {
    return {key: `image-${this.state.images.length}-${Date.now()}`, value: {url: ''}}
  }

  onImagesClear(index) {
    let images = this.state.images;
    if (index >= images.length) return;
    images.splice(index, 1);
    this.setState({
      images
    })
  }

  submit() {
    let data = {
      token: this.state.token,
      active: this.state.active,
      commission_bps: Api.Utils.dollarToCents(this.state.commission_bps),
      email_address: this.state.email_address,
      phone_number: this.state.phone_number,
      email_description: this.state.email_description,
      charitable: !!this.state.charitable,
      hand_made: !!this.state.hand_made,
      eco_friendly: !!this.state.eco_friendly,
      made_in_city: this.state.made_in_city
    };

    data.address = {
      name: this.state.address_name,
      address1: this.state.address_address1,
      address2: this.state.address_address2,
      postal_code: this.state.address_postal_code,
      city: this.state.address_city,
      state: (this.state.address_state && this.state.address_state.value) || null,
      instructions: this.state.address_instructions,
    };

    if (this.state.email_hero_image) {
      data.email_hero_image = this.state.email_hero_image;
    }

    if (this.state.minimum_order_amount_dollars) {
      data.minimum_order_amount_cents = Api.Utils.dollarToCents(this.state.minimum_order_amount_dollars);
    }

    data.images = this.state.images
      .filter((image) => {
        return !!(image && image.value && image.value.url)
      })
      .map((image) => {
        return image.value
      });

    if (this.state.made_in) {
      data.made_in = this.state.made_in.value;
    }

    if (this.state.made_in_state) {
      data.made_in_state = this.state.made_in_state.value;
    }

    return Api.saveBrand(data)
      .catch(error => {
        alert(createAlertMessage(error));
        this.setState({
          errorView: this.createErrorView(null, null, error)
        });
      });
  }

  handleSubmit(e) {
    e.preventDefault();
    const errorView = this.createErrorView();

    if (errorView) {
      this.setState({
        errorView,
      });
    } else {
      return this.submit();
    }
  }

  onAddressStateChange(selectedState) {
    this.setState({
      address_state: selectedState
    });
  }

  createErrorView(error) {
    let emailErrorKey = getErrorKey('email_address');
    let messages = [];
    if (this.state[emailErrorKey]) {
      messages.push('Invalid email field');
    }

    if (messages.length === 0 && !error) {
      return null;
    }

    let view;
    if (messages.length !== 0) {
      let listItems = messages.map((m, index) => {
        let key = `error-message-${index}`;
        return <li key={key}>{m}</li>
      });
      view = <ul>{listItems}</ul>;
    } else {
      view = (error && error.message && error.message.message) || 'There was an error saving the content';
    }

    return (
      <div className="admin-error-message">
        {view}
      </div>);
  }

  render() {
    const emailErrorKey = getErrorKey('email_address');
    let emailErrorClass = (this.state[emailErrorKey]) ? ErrorClass : '';

    return (
      <div className="brand-logistics-editor">
        <form className="admin-editor" onSubmit={this.handleSubmit}>
          <h2>Brand Logistic Info</h2>
          <div className="form-group">
            <div className="line">
              <label htmlFor="active" className="label">Active</label>
              <InputField onInput={this.onInput}
                          type="checkbox"
                          name="active"
                          defaultChecked={this.state.active}/>
            </div>

            <div className="line">
              <label htmlFor="email_address" className="label">E-Mail</label>
              <EmailField placeholder="Email Address"
                          className={emailErrorClass}
                          name="email_address"
                          onInput={this.onInput}
                          value={this.state.email_address}
                          required={false}/>
            </div>

            <div className="line">
              <label htmlFor="phone_number" className="label">Phone Number</label>
              <InputField name="phone_number"
                          placeholder="Phone Number"
                          onInput={this.onInput}
                          value={this.state.phone_number}/>
            </div>

            <div className="line">
              <label htmlFor="commission_bps" className="label">Commission %</label>
              <InputField onInput={this.onInput}
                          name="commission_bps"
                          type="tel"
                          value={this.state.commission_bps}
                          placeholder="Commission %"/>
            </div>

            <div className="line">
              <label htmlFor="minimum_order_amount_dollars" className="label">Min. Order $</label>
              <InputField onInput={this.onInput}
                          name="minimum_order_amount_dollars"
                          type="number"
                          value={this.state.minimum_order_amount_dollars}
                          placeholder="Minimum Order $"/>
            </div>

            <div className="line">
              <label htmlFor="made_in" className="label">Made In Country</label>
              <Select
                name="made_in"
                clearable={true}
                value={this.state.made_in}
                options={COUNTRIES}
                onChange={this.onCountryOriginChange}
              />
            </div>

            <div className="line">
              <label htmlFor="from" className="label">Made In State</label>
              <Select
                name="from"
                clearable={true}
                disabled={!this.state.secondaryOriginEnabled}
                value={this.state.made_in_state}
                options={this.state.secondaryOptions}
                onChange={this.onSecondaryOriginChange}
              />
            </div>

            <div className="line">
              <label htmlFor="minimum_order_amount_dollars" className="label">Made in City</label>
              <InputField onInput={this.onInput}
                          disabled={!this.state.made_in_state}
                          name="made_in_city"
                          type="text"
                          value={this.state.made_in_city}
                          placeholder="Made in City"/>
            </div>

            <div className="line checkboxes">
              <InputField onInput={this.onInput}
                          type="checkbox"
                          name="hand_made"
                          defaultChecked={this.state.hand_made}/>
              <label className="label">Handmade</label>
              <InputField onInput={this.onInput}
                          type="checkbox"
                          name="eco_friendly"
                          defaultChecked={this.state.eco_friendly}/>
              <label className="label">Eco-Friendly</label>
              <InputField onInput={this.onInput}
                          type="checkbox"
                          name="charitable"
                          defaultChecked={this.state.charitable}/>
              <label className="label">Charitable</label>
            </div>

            <div className="line line-block">
              <label className="label" htmlFor="images">Images</label>
              <MultipleField name="images"
                                       component={ImageField}
                                       inputs={this.state.images}
                                       onNew={this.onImagesNew}
                                       onClear={this.onImagesClear}
                                       onChange={this.onImagesChange}/>
            </div>

          </div>

          <h2>Email Campaign Details</h2>
          <div className="form-group">
            <div className="line">
              <label className="label" htmlFor="email_hero_image">Email Hero Image</label>
              <ImageField onInput={this.onInput}
                                    type="text"
                                    name="email_hero_image"
                                    value={this.state.email_hero_image}
                                    placeholder="Email hero image"/>
            </div>

            <div className="line line-block">
              <label htmlFor="email_description" className="label">Email Description</label>
              <InputField onInput={this.onInput}
                          name="email_description"
                          type="textarea"
                          value={this.state.email_description}
                          placeholder="Email Description"/>
            </div>
          </div>
          <h2>Address</h2>
          <div className="form-group">
            <div className="line">
              <label htmlFor="address_name" className="label">Name</label>
              <InputField onInput={this.onInput}
                          name="address_name"
                          type="text"
                          value={this.state.address_name}
                          placeholder="Name"/>
            </div>

            <div className="line">
              <label htmlFor="address_line1" className="label">Address Line 1</label>
              <InputField onInput={this.onInput}
                          name="address_address1"
                          type="text"
                          value={this.state.address_address1}
                          placeholder="Address Line 1"/>
            </div>

            <div className="line">
              <label className="label">Address line 2</label>
              <InputField onInput={this.onInput}
                          name="address_address2"
                          type="text"
                          value={this.state.address_address2}
                          placeholder="Address Line 2"/>
            </div>

            <div className="line">
              <label htmlFor="address_postalcode" className="label">Postal Code</label>
              <InputField onInput={this.onInput}
                          name="address_postal_code"
                          type="text"
                          value={this.state.address_postal_code}
                          placeholder="Postal Code"/>
            </div>

            <div className="line">
              <label htmlFor="address_city" className="label">City</label>
              <InputField onInput={this.onInput}
                          name="address_city"
                          type="text"
                          value={this.state.address_city}
                          placeholder="City"/>
            </div>

            <div className="line">
              <label htmlFor="address_state" className="label">State</label>
              <Select
                name="address_state"
                clearable={true}
                value={this.state.address_state}
                options={USA_STATES}
                onChange={this.onAddressStateChange}
              />
            </div>

            <div className="line line-block">
              <label htmlFor="address_instructions" className="label">Instructions</label>
              <InputField onInput={this.onInput}
                          name="address_instructions"
                          type="textarea"
                          value={this.state.address_instructions}
                          placeholder="Instructions"/>
            </div>
          </div>
          {this.state.errorView}
          <div>
            <Button onClick={this.handleSubmit}>Save</Button>
          </div>
        </form>
      </div>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/components/BrandLogisticsEditor/index.jsx
 **/